import { useState } from 'react';
import { useWebhooks, useCreateWebhook, useDeleteWebhook } from '../hooks/useApi';
import type { WebhookCreateResponse, WebhookEvent } from '../types';
import { WEBHOOK_EVENTS } from '../types';
import { ConfirmModal } from '../components/ConfirmModal';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../hooks/useFormatDate';

export function WebhooksPage() {
  const { t } = useTranslation('settings');
  const formatDate = useFormatDate();
  const { data: webhooks, isLoading } = useWebhooks();
  const { mutate: createWebhook, isPending: creating } = useCreateWebhook();
  const { mutate: deleteWebhook } = useDeleteWebhook();

  const [showCreate, setShowCreate] = useState(false);
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<WebhookEvent[]>([...WEBHOOK_EVENTS]);
  const [createdWebhook, setCreatedWebhook] = useState<WebhookCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  function toggleEvent(ev: WebhookEvent) {
    setSelectedEvents((prev) => (prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]));
  }

  function handleCreate() {
    setError(null);
    const trimmed = url.trim();
    if (!/^https:\/\/.+/i.test(trimmed)) { setError(t('webhooks.invalidUrl')); return; }
    if (selectedEvents.length === 0) { setError(t('webhooks.noEvents')); return; }
    createWebhook({ url: trimmed, events: selectedEvents }, {
      onSuccess: (data) => {
        setCreatedWebhook(data);
        setUrl('');
        setSelectedEvents([...WEBHOOK_EVENTS]);
        setShowCreate(false);
      },
      onError: (e: unknown) => setError(e instanceof Error ? e.message : t('webhooks.createFailed'))
    });
  }

  function handleCopy(secret: string) {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
      <div className="page-narrow">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('webhooks.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('webhooks.subtitle')}</p>
          </div>
          <button
            onClick={() => { setShowCreate(true); setError(null); }}
            className="rounded-xl bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] flex-shrink-0"
          >
            {t('webhooks.addEndpoint')}
          </button>
        </div>

        {/* Created secret banner (shown once) */}
        {createdWebhook && (
          <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
            <p className="text-xs font-semibold text-emerald-800 mb-1">{t('webhooks.endpointCreated')}</p>
            <p className="text-xs text-emerald-700 mb-2">{t('webhooks.secretOnce')}</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-lg bg-white border border-emerald-200 px-3 py-2.5 text-sm font-mono text-gray-900 select-all break-all">
                {createdWebhook.secret}
              </code>
              <button
                onClick={() => handleCopy(createdWebhook.secret)}
                className="rounded-lg border border-emerald-200 bg-white px-4 py-2.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors flex-shrink-0"
              >
                {copied ? t('common:buttons.copied') : t('common:buttons.copy')}
              </button>
            </div>
            <p className="mt-2 text-xs text-emerald-700">{t('webhooks.signatureHint')}</p>
            <button onClick={() => setCreatedWebhook(null)} className="mt-3 text-xs text-emerald-600 hover:text-emerald-800">
              {t('webhooks.dismiss')}
            </button>
          </div>
        )}

        {/* Create form */}
        {showCreate && (
          <div className="mb-5 rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <label className="block text-xs font-medium text-gray-700 mb-2">{t('webhooks.endpointUrl')}</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/hooks/badges"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-700 mb-2">{t('webhooks.events')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {WEBHOOK_EVENTS.map((ev) => (
                  <label key={ev} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={selectedEvents.includes(ev)}
                      onChange={() => toggleEvent(ev)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-400"
                    />
                    <code className="font-mono text-primary-700">{ev}</code>
                  </label>
                ))}
              </div>
            </div>
            {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
            <div className="mt-4 flex gap-2">
              <button
                onClick={handleCreate}
                disabled={creating}
                className="rounded-lg bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {creating ? t('webhooks.creating') : t('webhooks.create')}
              </button>
              <button
                onClick={() => { setShowCreate(false); setUrl(''); setError(null); }}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {t('webhooks.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (<div key={i} className="h-20 rounded-2xl border border-gray-200 bg-white animate-pulse" />))}
          </div>
        ) : webhooks && webhooks.length > 0 ? (
          <div className="space-y-3">
            {webhooks.map((w) => (
              <div key={w.id} className="flex items-start justify-between rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-card">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-sm font-mono text-gray-800 break-all">{w.url}</code>
                    {!w.active && (
                      <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600">
                        {t('webhooks.disabled')}
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {w.events.map((ev) => (
                      <span key={ev} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-mono text-gray-500">
                        {ev === '*' ? t('webhooks.allEvents') : ev}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1.5 flex items-center gap-4 text-xs text-gray-400">
                    <span>{t('webhooks.created', { date: formatDate(w.createdAt) })}</span>
                    {w.lastDeliveryAt && (
                      <span>{t('webhooks.lastDelivery', { date: formatDate(w.lastDeliveryAt), status: w.lastStatus ?? '—' })}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setConfirmAction({
                    title: t('webhooks.deleteTitle'),
                    message: t('webhooks.deleteMessage', { url: w.url }),
                    onConfirm: () => { deleteWebhook(w.id); setConfirmAction(null); }
                  })}
                  className="rounded-lg border border-red-100 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors flex-shrink-0"
                >
                  {t('webhooks.delete')}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-card">
            <p className="text-sm text-gray-400">{t('webhooks.empty')}</p>
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction?.title ?? ''}
        message={confirmAction?.message ?? ''}
        confirmLabel={t('webhooks.confirmDelete')}
        onConfirm={() => confirmAction?.onConfirm()}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
